import { Button } from '@billy/design-system'
import { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { EInvoiceStatus } from '@features/eInvoicing/constants'
import {
  EInvoiceApprovalConfirmationModal,
  eInvoiceApprovalConfirmationModalId,
} from '@features/eInvoicing/elements/EInvoiceApprovalConfirmationModal'
import { useEInvoiceApproveAction } from '@features/eInvoicing/hooks/useEInvoiceApproveAction'
import type { EInvoiceLifecycleTrackingSource } from '@views/receipts/enums/tracking'

interface EInvoiceApproveButtonProps {
  attachmentId?: string
  disabled?: boolean
  fullWidth?: boolean
  onProcessingChange?: (isProcessing: boolean) => void
  onSuccess?: () => void
  previousStatus?: EInvoiceStatus | null
  trackingSource: EInvoiceLifecycleTrackingSource
}

export const EInvoiceApproveButton = ({
  attachmentId,
  disabled = false,
  fullWidth = false,
  onProcessingChange,
  onSuccess,
  previousStatus,
  trackingSource,
}: EInvoiceApproveButtonProps): ReactElement | null => {
  const { t } = useTranslation()
  const { handleApproveClick, handleConfirmApproval, isProcessing } = useEInvoiceApproveAction({
    attachmentId,
    onSuccess,
    previousStatus,
    trackingSource,
  })

  useEffect(() => {
    onProcessingChange?.(isProcessing)

    return () => {
      onProcessingChange?.(false)
    }
  }, [isProcessing, onProcessingChange])

  if (!attachmentId) {
    return null
  }

  return (
    <>
      <Button disabled={disabled} fullWidth={fullWidth} loading={isProcessing} onClick={handleApproveClick}>
        {t('receipts.e_invoice.approve')}
      </Button>
      <EInvoiceApprovalConfirmationModal
        id={eInvoiceApprovalConfirmationModalId}
        isProcessing={isProcessing}
        onConfirm={handleConfirmApproval}
        size="s"
      />
    </>
  )
}
